<template>
  <simple-activity :show-header="false">


    <!-- CONTENT -->
    <b-container class="pt-1 px-2" fluid="true">

      <b-row no-gutters>
        <b-col>
          <div class="d-block text-center">
            {{ $tr("cloneActivity.CopyFrom:", "Copy From:") }} {{ message }}
          </div>
        </b-col>
      </b-row>

      <b-row class="w-100">
        <b-col>
          <b-form-group label="" v-slot="{ ariaDescribedby }" v-if="canChain" class="py-5">
            <b-form-radio-group
                id="radio-group-1"
                v-model="selectedMode"
                :options="modeOptions"
                stacked
                :aria-describedby="ariaDescribedby"
                name="radio-clone-options"
            ></b-form-radio-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row no-gutters v-if="hasDeepClones">
        <b-col cols="12">
          {{ $tr("decideHowToMakeCopyOfThisRecord", "Decide how to make copy ot this record") }}
        </b-col>
      </b-row>

      <b-row v-for="field in deepCopyFields" :key="field.fieldName" no-gutters>
        <b-col cols="auto">
          {{ field.fieldLabel }}
          <select class="form-select form-control" v-model="field.selectedOption">
            <option selected value="deep">{{ $tr("makeNewCopy", "Make a new copy") }}</option>
            <option value="shallow">{{ $tr("makeLinkToTheSameRecord", "Make a link to the same record") }}</option>
          </select>
        </b-col>
      </b-row>

      <b-row no-gutters v-if="hasRenderers" class="mt-5">
        <b-col>
          {{ $tr("setUniqueValuesForTheNewRecord", "Set unique values for the new record") }}
        </b-col>
      </b-row>

      <b-row no-gutters ref="uniques" class="mt-1">

      </b-row>
      <b-row v-if="!isValid" no-gutters>
        <div class="text-center" style="color: red;">{{ errorMessage }}</div>
      </b-row>
    </b-container>
    <!-- CONTENT END-->

    <template v-slot:bottomLine>
      <div v-if="formModel && formModel.generalFeedback" class="general-feedback">
        <label>{{ formModel.generalFeedback }}</label>
      </div>
    </template>

    <template v-slot:footer>
      <!-- extra footer buttons -->
      <FooterButton sm-style="link"
                    button-style="width: unset" class="px-0 col-auto" type="Add"
                    :label="selectedMode === 'copy' ? duplicateRecordLabel : createVersionLabel"
                    :disabled="!isValid || lockedDuringRpcRequest"
                    @add="add">
      </FooterButton>
    </template>
  </simple-activity>
</template>

<script>
import Activity from "@/activity/Activity";
import Perms from "@/server/Perms";
import PermissionsSet from "@/server/PermissionsSet";
import FooterButton from "@/components/navbar/FooterButton";
import Log from "@/utils/Log";
import DialogManager from "@/utils/DialogManager";
import SimpleActivity from "@/activity/templates/layouts/SimpleActivity";
import FormsAI from "@/components/forms/FormsAI";
import _ from "lodash";
import FormModel from "@/components/forms/FormModel";
import {getComponentClass} from "@/components/forms/renderers/Renderers";
import FormConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormConfig";
import CloneOptions from "@/server/com/silkplm/rpc/CloneOptions";
import {cloneEntity} from "@/utils/Objects";
import tr from "../../utils/Tr";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";
import Season from "@/server/com/silkplm/model/plm/Season";
import {
  findFieldValueFromCompanyCompanyConfigurationForm, findFieldValueFromCompanyCompanyConfigurationFormField
} from "@/components/forms/customizations/plm/EntityEditFormService";


export default {
  name: "CloneActivity",
  components: {SimpleActivity, FooterButton},
  created() {
    let form = this.form;
    let entityInstance = this.entityInstance;
    this.overwrites = [];

    this.resolve = null;
    this.canChain = form.canChain;
    this.title = $tr('cloneActivity.copyOf', 'Copy of #{1}', form.formTitle);
    let label = FormsAI.getEntityLabel(form, entityInstance);
    this.localInstance = cloneEntity(entityInstance);
    this.form = form;

    this.message = label;
    this.modeOptions[0].text = $tr('cloneActivity.duplicateAsUnrelated', 'Duplicate as unrelated #{1}', form.entityName.toLowerCase());
    this.modeOptions[1].text = $tr('cloneActivity.createVersionOfThe', 'Create version of the #{1}', form.entityName.toLowerCase());
    this.modeOptions[2].text = $tr('cloneActivity.duplicateWithAllData2',
        'Duplicate with all data for new Season (EAN, SKU Number, Order Number) #{1}',
        form.entityName.toLowerCase());
    if (!this.canChain) {
      this.selectedMode = 'copy';
    }
    this.formModel = new FormModel(this.form);
    this.formModel.editMode(this.localInstance, 'EDIT');
  },
  mounted() {
    if (!this.formModel) {
      this.formModel = new FormModel(this.form);
    }
    this.addUniqueInputs();
    this.$nextTick(() => {
      this.afterMounted();
    });
  },
  beforeDestroy() {
    if (this.renderers) {
      for (let input of this.renderers) {
        input.beforeUnmount?.();
        input.$destroy?.();
        const index = this.$children.indexOf(input);
        if (index > -1) {
          this.$children.splice(index, 1);
        }
        setTimeout(() => {
          try {
            input?.afterUnmount?.();
          } finally {
            setTimeout(() => {
              input.onDestroy?.();
            });
          }
        });
        this.input = null;
      }
    }
  },
  methods: {

    ...Activity.methods,

    getTitle() {
      return $tr('duplicate', 'Duplicate #{1}', this.form.entityName);
    },

    async canClose() {
      return true;
    },

    afterMounted() {
      this.readyToValidate = true;
    },

    add() {
      let cloneOptions = new CloneOptions();
      cloneOptions.mode = this.selectedMode;
      cloneOptions.cloneAttachments = true;
      cloneOptions.cloneGallery = true;
      cloneOptions.rawOverwrites = JSON.stringify(this.localInstance);
      cloneOptions.overwrites = this.overwrites;
      cloneOptions.deepClones = [];
      for (let field in this.form.fieldCloneOptions) {
        let fieldOpts = this.form.fieldCloneOptions[field];
        if (fieldOpts.doDeepCopy && !fieldOpts.doShallowCopy) {
          cloneOptions.deepClones.push(field);
        }
      }
      //let selection = {fieldName: field, fieldLabel, selectedOption:'deep'};
      for (let dcf of this.deepCopyFields) {
        if (dcf.selectedOption === 'deep') {
          cloneOptions.deepClones.push(dcf.fieldName);
        }
      }

      this.setResult(cloneOptions);
      this.close();
    },

    addUniqueInputs() {
      this.renderers = [];
      let form = this.form;
      for (let fld of form.fields) {
        if (fld.inUniqueConstraint && !form.ignoredUniqueFieldsInClone.find(f => f === fld.fieldName)) {
          if (fld.alwaysShowInCloneActivity) {
            this.setupUniqueOverwriteField(fld);
            return;
          }
          if (fld.allowNull) {
            this.localInstance[fld.fieldName] = null;
            this.overwrites.push(fld.fieldName);
            return;
          }
          if (fld.emptyExcludedFromUnique) {
            this.localInstance[fld.fieldName] = "";
            this.overwrites.push(fld.fieldName);
            return;
          }

          this.setupUniqueOverwriteField(fld);
        }
      }
      this.formModel.setFieldsReadOnly(false, 'EDIT', null);

      for (let field in this.formModel.formConfig.fieldCloneOptions) {
        let fieldOpts = this.formModel.formConfig.fieldCloneOptions[field];
        if (fieldOpts.doDeepCopy && fieldOpts.doShallowCopy) {
          let fieldDef = this.form.fields.find(f => f.fieldName === field);
          let fieldLabel = field;
          if (fieldDef) {
            fieldLabel = fieldDef.fieldLabel;
          } else {
            if (fieldOpts.fieldName) {
              fieldLabel = fieldOpts.fieldName;
            }
          }

          let selection = {fieldName: field, fieldLabel, selectedOption: 'deep'};
          this.deepCopyFields.push(selection);
          this.hasDeepClones = true;
        }
      }
    },

    /**
     * @param fieldConfig {FormFieldConfig}
     */
    setupUniqueOverwriteField(fieldConfig) {
      let form = this.form;
      const clazz = getComponentClass(fieldConfig.renderer, this.$router);
      //console.log(this.formConfig.entityPlural + "==>" + this.masterFormModel?.formConfig?.entityPlural);

      let masterForm = this.formModel;
      let validatedField = this.formModel.validatedFieldsByName.get(fieldConfig.fieldName);
      validatedField.masterFormModel = masterForm;

      const input = new clazz({
        propsData: {
          fieldConfig,
          validatedField,
          context: null,
          mode: "EDIT",
          parentForm: this.form,
          masterFormModel: masterForm,
        }
      });

      this.$children.push(input);
      input.$parent = this;
      input.$root = this.$root;
      input.$mount?.();

      input.setEditorMode?.(true);
      input.setReadOnly?.(false);

      let div = document.createElement("div");
      div.setAttribute("class", "col-12");
      this.$refs.uniques.append(div);
      div.appendChild(input.$el);
      this.renderers.push(input);
      input.$on('change-parent', () => {
        this.handleChangeSelectedMode(this.selectedMode);
      });
      this.overwrites.push(fieldConfig.fieldName);
      this.hasRenderers = true;
    },

    async handleChangeSelectedMode(selectedMode) {
      try {
        this.lockedDuringRpcRequest = true;
        if (!this.readyToValidate) {
          return;
        }
        this.isValid = true;
        if (selectedMode === "copyAllData") {
          if (this.entityInstance instanceof ProductInSeason) {
            let seasonId = this.entityInstance.seasonId;
            let seasonEntity = new Season();
            await seasonEntity.load(seasonId);
            if (this.renderers?.[0]?.$data) {
              let selectedSeasonName = this.renderers[0].$data.formattedValue;
              if (selectedSeasonName === seasonEntity.season) {
                this.isValid = false;
                this.errorMessage = $tr("cloneActivity.errorMessage",
                    "This option is available for a different season than the one currently selected on the project.");
                return;
              }

              let selectedSeason = await Rpc.plm.services.ProductInSeasonService.findSeasonByName(selectedSeasonName)
              if (selectedSeason) {
                let versionChart = await Rpc.Versioning.getFamilyTree(ProductInSeason.getRemoteClass(), this.entityInstance.id);
                let existProjectForSeason = false;
                if (versionChart.children.length) {
                  versionChart.children.forEach((children) => {
                    if (children.item.seasonId === selectedSeason.id) {
                      if (children.item.id !== this.entityInstance.id) {
                        existProjectForSeason = true;
                        this.errorMessage = $tr("cloneActivity.uniqueErrorMessage",
                            "Project with number: #{1} already exists for season: #{2}", children.item.numberOfProduct, selectedSeasonName);
                      }
                    }
                  });
                  if (existProjectForSeason) {
                    this.isValid = false;
                    return;
                  }
                }
              }
            }
          }
        }
        if (this.renderers[1]?.$refs?.input) {
          if (this.entityInstance instanceof ProductInSeason) {
            let subSeasons = await Rpc.plm.services.ProductInSeasonService.countSubSeasonsForSeason(this.renderers[0].$data.formattedValue);
            if (subSeasons === 0) {
              let projectFormConfig = FormsLoader.detectFormConfig(ProductInSeason.getRemoteClass());
              let subSeasonFormFieldConfig = projectFormConfig.fields.filter(field => field.fieldName === "subSeasonId")[0];
              let subSeasonBaseRequiredField = findFieldValueFromCompanyCompanyConfigurationFormField(
                  projectFormConfig, subSeasonFormFieldConfig, "baseRequiredField");
              this.isValid = !subSeasonBaseRequiredField
              if (!this.isValid) {
                this.errorMessage = $tr("cloneActivity.subSeason.isRequired",
                    "For the Project, a Season is required – you must choose one.");
              }
            } else {
              let formattedVal = this.renderers[1].$refs.input.$data.formattedValue;
              if (formattedVal && formattedVal?.length) {
                this.isValid = true;
                this.errorMessage = null;
              } else {
                this.isValid = false;
              }
            }
            this.renderers[1].$refs.input.forceSetValidate(!this.isValid);
          }
        }
      } finally {
        this.lockedDuringRpcRequest = false;
      }
    }
  },
  watch: {
    "selectedMode": async function (val) {
      this.isValid = true;
      await this.handleChangeSelectedMode(val);
    },
  },

  props: {
    form: {required: true, type: FormConfig},
    entityInstance: {required: true},
    entityId: {required: true}
  },

  data: () => ({
    duplicateRecordLabel: $tr("cloneActivity.duplicateRecord", "Duplicate record"),
    createVersionLabel: $tr("cloneActivity.createVersion", "Create version"),
    lockedDuringRpcRequest: false,
    errorMessage: null,
    isValid: true,
    formModel: null,
    deepCopyFields: [],
    hasDeepClones: false,
    hasRenderers: false,
    unsavedChanges: false,
    permissions: new PermissionsSet(),
    isYesDanger: false,
    title: "title",
    message: "message",
    canChain: false,
    resolve: null,
    canSave: true,
    maxLength: 100,
    cloneAttachments: true,
    cloneGallery: true,
    localInstance: null,
    modeOptions: [
      {text: 'Duplicate as unrelated record', value: 'copy'},
      {text: 'Create new version', value: 'chain'},
      {text: 'Duplicate with all data for new Season (EAN, SKU Number, Order Number)', value: 'copyAllData'},
    ],
    selectedMode: "copy",
    readyToValidate: false
  })
}
</script>
